import cls from "classnames";
import { getTemporalLabel } from "@qoohoo/common/helpers/channel";
import Spinner from "@qoohoo/common/library/loader/Spinner";
import { MultiCurrency } from "@qoohoo/common/utils/currency/index";
import withJoinButtonWithTaxDetails from "../withJoinButtonWithTaxDetails";

const _JoinTelegramButton = ({
  channelDetails,
  userChannelDetail,
  isLoading,
  isDisabled = false,
  className,
  showTaxDetails = false,
  couponState = {},
  onClick = () => {},
  custom_button_label
}) => {
  const ctaButtonText =
    userChannelDetail.cta_button?.button_text ||
    channelDetails.cta_button?.button_text;
  const ctaButtonAction =
    userChannelDetail.cta_button?.action || channelDetails.cta_button?.action;
  const isSubscribed = userChannelDetail.is_channel_member;
  const isChannelArchived = channelDetails.is_archived;
  const isChannelEnded = channelDetails.is_ended;

  const isRecurring = channelDetails.type === "recurring";
  const hasMultiplePlans = channelDetails?.plans?.length > 1;

  const showTaxInfo = !(isRecurring && hasMultiplePlans) && showTaxDetails;

  const isOneTimePlan = channelDetails.type?.toLowerCase() === "one_time";

  const firstChannelPlan = channelDetails?.plans?.[0];

  let channelFee;

  if (isOneTimePlan) {
    channelFee = channelDetails?.channel_fee;
  } else {
    channelFee =
      firstChannelPlan?.amount_after_discount || firstChannelPlan?.amount;
  }

  const couponDiscount = isOneTimePlan
    ? couponState.data
    : couponState.data?.[firstChannelPlan?.uid];
  const currency = channelDetails?.currency;

  const buttonLabel = getButtonLabel();
  const buttonIcon = getButtonIcon();

  function getChannelFeeLabel() {
    let label = "";

    let finalAmount = channelFee;

    if (couponDiscount) {
      finalAmount = couponDiscount.amount_after_coupon_discount;
    }

    const currencyAmount = MultiCurrency.formatAmountWithSymbol(
      finalAmount,
      currency
    );

    if (isRecurring) {
      if (hasMultiplePlans) {
        label = custom_button_label?.recurring || "Subscribe Now";
      } else {

        if (custom_button_label?.default) {
          label = showTaxInfo ? custom_button_label?.default : (
            `${custom_button_label?.default} ${currencyAmount}`
          )
        } else {
          label = showTaxInfo ? "Subscribe Now" : `Subscribe for ${currencyAmount}`;
        }
      }
    } else if (finalAmount) {
      if (custom_button_label?.default) {
        label = `${custom_button_label?.default} ${currencyAmount}`;
      } else {
        label = `Pay ${currencyAmount}`;
      }
    } else {
      label = "Join for FREE";
    }

    return label;
  }

  function getButtonLabel() {
    let label = "";

    if (isLoading) {
      label = <Spinner size="xs" />;
    } else if (isChannelArchived) {
      label = "Channel archived";
    } else if (isChannelEnded) {
      label = "Channel ended";
    } else if (!isSubscribed) {
      label = getChannelFeeLabel();
    } else {
      label = "Extend Membership";
    }

    return label;
  }

  function getButtonIcon() {
    let icon = "";
    const isInviteLinkReady = userChannelDetail.telegram?.invite_link;

    if (isInviteLinkReady && isSubscribed) {
      icon = "/assets/icons/external-link.svg";
    }

    return icon;
  }

  function getButtonVariant(label, icon) {
    let button = null;

    const isButtonDisabled = isChannelArchived || isChannelEnded || isLoading;

    const showStrikeThroughAmount =
      !hasMultiplePlans && !isSubscribed && !showTaxDetails && couponDiscount;

    if (isButtonDisabled) {
      button = (
        <button
          className={cls(
            "relative w-full font-bold rounded-md outline-none focus:outline-none cursor-not-allowed",
            className,
            isLoading
              ? "bg-gradient-to-t from-[#229ED9] to-[#2AABEE] py-1.5"
              : "bg-[#1B1F22] py-3"
          )}
          disabled={true}
        >
          {isLoading ? <Spinner size="xs" /> : label}
        </button>
      );
    } else {
      let sublabel;

      if (isRecurring) {
        if (hasMultiplePlans) {
          sublabel = "Multiple plans available";
        } else {
          sublabel = getTemporalLabel(
            {
              channelType: channelDetails.type,
              start_date: null,
              planDetails: {
                planType: channelDetails?.plans?.[0]?.plan_type,
                interval: channelDetails?.plans?.[0]?.interval,
                duration: channelDetails?.plans?.[0]?.duration,
              },
            },
            false
          ).toLowerCase();
        }
      }

      button = (
        <button
          className={cls(
            "text-center mx-auto relative w-full py-3 hover:opacity-90 bg-gradient-to-t from-[#229ED9] to-[#2AABEE]",
            "outline-none focus:outline-none rounded-md",
            className,
            isDisabled && "opacity-60 hover:opacity-60 cursor-not-allowed",
          )}
          disabled={isDisabled}
          onClick={onClick}
        >
          <div className="font-bold leading-[18px] relative">
            {label}
            {showStrikeThroughAmount && (
              <span className="line-through opacity-50 font-normal ml-2 top-1/2 -translate-y-1/2 absolute text-xs">
                {`${MultiCurrency.formatAmountWithSymbol(
                  channelFee,
                  currency
                )}`}
              </span>
            )}
          </div>
          <div className="text-[11px] leading-[14px] font-normal capitalize">
            {sublabel}
          </div>
        </button>
      );
    }
    return button;
  }

  return buttonLabel ? getButtonVariant(buttonLabel, buttonIcon) : null;
};

export default function JoinTelegramButton (props) {
  const {
    channelDetails,
    isLoading,
    showTaxDetails = false,
  } = props;

  const isRecurring = channelDetails.type === "recurring";
  const hasMultiplePlans = channelDetails?.plans?.length > 1;

  const _showTaxDetails = showTaxDetails && !(isRecurring && hasMultiplePlans) && !isLoading;

  const JoinButtonComponent = _showTaxDetails ? withJoinButtonWithTaxDetails(_JoinTelegramButton) : _JoinTelegramButton;

  return <JoinButtonComponent {...props} />;
};
